import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { FormProps } from '@leafwell/components';
import type {
  Outreach_Partners_Order_By,
  Outreach_Google_Locations_Order_By,
  PartnersLocationsSearchQueryVariables,
  PartnersLocationsSearchQuery,
} from 'graphql/research/types-and-hooks';
import type {
  LocationCoordinates,
  LocationItem,
  LocationItems,
} from 'lib/queries/locations';
import type { WordpressContext } from 'contexts/wordpress-provider';
import type { PartnersLocationsResult } from 'lib/queries/partners';
import type { LocationsMapProps } from './locations-map';
import Image from 'next/image';

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  useFormContext,
  useUniqueId,
  useToast,
  Button,
  Card,
  Carousel,
  Form,
  Link,
  SelectFieldNativeControlled as Select,
  SwitchFieldControlled as Switch,
  TextFieldControlled as TextField,
  ToggleFieldControlled as Toggle,
  Wrap,
} from '@leafwell/components';

import { ApolloProvider, useApolloClient } from '@apollo/client';

import { useWordPressContext } from 'contexts/wordpress-provider';
import {
  refetchPartnersLocationsSearchQuery,
  Order_By,
} from 'graphql/research/types-and-hooks';
import { initializeWpApollo, queryResearch } from 'lib/apollo';
import { useCurrentState } from 'hooks/useCurrentState';
import { useStatesList } from 'hooks/useStatesList';
import { useUserLocation } from 'hooks/useUserLocation';
import { debounce } from 'lib/utils/debounce';
import HeadingBody from './heading-body';
import Loader from './loader';
import LocationsMap from './locations-map';

import statesCenter from 'lib/data/states-center.json';
import {
  filterLocationsByDistance,
  sortLocationsByDistance,
} from 'lib/queries/locations';
import LocationCard from './location-card';

enum FILTER_KEYS {
  STATE = 'state',
  USER_LOCATION = 'current_location',
  OPEN = 'open',
}

enum SORT_KEYS {
  NAME = 'name',
  RATING = 'rating',
  RATING_COUNT = 'userRatingsTotal',
  DISTANCE = 'distance',
}

export type SearchFormFields = {
  location: string;
  open: boolean;
  search: string;
  sort: `${SORT_KEYS}`;
};

export type LocationsSearchProps = Pick<PartnersLocationsResult, 'locations'> &
  Pick<LocationsMapProps, 'variant'>;

export type LocationsSettings = {
  activeId?: LocationItem['id'];
  activeLocations: LocationItems;
  center: LocationCoordinates | null;
  filters: SearchFormFields;
  loading: boolean;
};

export type LocationsSettingsInit = Partial<LocationsSettings>;

export type LOCATIONS_FILTER_KEYS = 'location' | 'state' | 'search';

export type LocationsSettingsAction =
  | {
      type: 'reset';
      value: LocationsSettingsInit;
    }
  | {
      type: 'setCenter';
      value: LocationsSettings['center'];
    }
  | { type: 'updateActiveId'; value: LocationsSettings['activeId'] }
  | {
      type: 'updateSearch';
      value: Pick<LocationsSettings, 'center' | 'filters'>;
    }
  | {
      type: 'updateLocations';
      value: LocationsSettings['activeLocations'];
    };

export type SearchFormProps = Pick<WordpressContext, 'translations'> &
  Pick<FormProps, 'onSubmit'> & {
    defaultState?: string;
    onReset: () => void;
    userLocation?: LocationCoordinates;
  };

const SORT_PARAMS: Record<
  SORT_KEYS,
  {
    label: string;
    callback?: (args: unknown) => LocationItems;
    sort?: Partial<
      Pick<Outreach_Partners_Order_By, 'name' | 'google_location'>
    >;
  }
> = {
  [SORT_KEYS.NAME]: { label: 'Name', sort: { name: Order_By.Asc } },
  [SORT_KEYS.RATING]: {
    label: 'Highest rated',
    sort: {
      google_location: {
        rating: Order_By.Desc,
      } as Outreach_Google_Locations_Order_By,
    },
  },
  [SORT_KEYS.RATING_COUNT]: {
    label: 'Most reviewed',
    sort: {
      google_location: {
        user_ratings_total: Order_By.Desc,
      } as Outreach_Google_Locations_Order_By,
    },
  },
  [SORT_KEYS.DISTANCE]: {
    label: 'Distance',
    callback: sortLocationsByDistance,
  },
};

function filterLocationsByOpen({
  locations,
}: {
  locations: LocationItems;
}): LocationItems {
  const today = new Date();
  const weekday = today.getDay();
  const timestamp = today.getTime();

  return locations.filter(({ openingHours }) => {
    const todaySchedule = openingHours[weekday];

    return todaySchedule?.open <= timestamp && todaySchedule?.close > timestamp;
  });
}

function filterLocationsByState({
  state,
  locations,
}: {
  state: LocationItem['state'];
  locations: LocationItems;
}): LocationItems {
  return locations.filter(location => location.state === state);
}

function filterLocations({
  activeLocations,
  center,
  filters,
}: Pick<
  LocationsSettings,
  'activeLocations' | 'center' | 'filters'
>): LocationItems {
  let newLocations = activeLocations;

  // filter locations by current location filter
  if (filters.location.length) {
    newLocations =
      filters.location === FILTER_KEYS.USER_LOCATION
        ? filterLocationsByDistance({
            center,
            locations: newLocations,
          })
        : filterLocationsByState({
            locations: newLocations,
            state: filters.location,
          });
  }

  // check if filtering open locations
  if (filters.open) {
    newLocations = filterLocationsByOpen({
      locations: newLocations,
    });
  }

  // check if current sort filter requires custom sorting
  if (SORT_PARAMS[filters.sort]?.callback) {
    newLocations = SORT_PARAMS[filters.sort].callback({
      center,
      locations: newLocations,
    });
  }

  return newLocations;
}

function getQueryVariables(
  filters: SearchFormFields,
): PartnersLocationsSearchQueryVariables {
  return {
    ...((SORT_PARAMS?.[filters.sort]?.sort
      ? { sort: SORT_PARAMS[filters.sort].sort }
      : {}) as Outreach_Partners_Order_By),
    ...(filters.search.length ? { search: `%${filters.search}%` } : {}),
  };
}

function getStateCenter(state: string): LocationCoordinates | null {
  return statesCenter.find(item => item.state === state) || null;
}

function initReducer(initialState: LocationsSettingsInit): LocationsSettings {
  const state = {
    activeId: initialState?.activeId || null,
    activeLocations: initialState?.activeLocations || [],
    center: initialState?.center || null,
    filters: initialState?.filters || null,
    loading: initialState?.loading || false,
  };

  const filteredLocations = filterLocations(state);
  let currCenter = state.center;

  if (filteredLocations.length === 1) {
    currCenter = filteredLocations[0];
  }

  if (!currCenter && filteredLocations.length <= 0) {
    currCenter = getStateCenter(state.filters.location);
  }

  return {
    ...state,
    activeLocations: filteredLocations,
    center: currCenter,
  };
}

function locationSettingsReducer(
  state: LocationsSettings,
  { type: actionType, value }: LocationsSettingsAction,
): LocationsSettings {
  switch (actionType) {
    case 'reset':
      return initReducer(value);
    case 'setCenter': {
      if (state.center !== value || state.center?.lat !== value?.lat) {
        return {
          ...state,
          center: value,
        };
      }

      return state;
    }
    case 'updateActiveId': {
      return {
        ...state,
        activeId: value,
        center: state.activeLocations.find(({ id }) => id === value)
          ? state.activeLocations.find(({ id }) => id === value)
          : state.center,
      };
    }
    case 'updateSearch': {
      return {
        ...state,
        ...value,
        loading: true,
      };
    }
    case 'updateLocations': {
      return initReducer({
        ...state,
        activeLocations: value,
        loading: false,
      });
    }
    default:
      return state;
  }
}

const SearchFormContent: React.FC<SearchFormProps> = memo(
  ({ onReset, onSubmit, translations, defaultState }) => {
    const {
      handleSubmit,
      formState: { isSubmitting, submitCount },
      reset,
      setValue,
      watch,
    } = useFormContext();
    const statesList = useStatesList();
    const filtersId = useUniqueId('map-filters');
    const [filtersVisible, toggleFilters] = useState(false);
    const currLocation = watch('location');

    const sortOptions = useMemo(() => {
      const options = Object.keys(SORT_PARAMS).map(value => ({
        label: translations[SORT_PARAMS[value].label],
        value,
      }));

      if (currLocation !== FILTER_KEYS.USER_LOCATION) {
        return options.filter(({ value }) => value !== SORT_KEYS.DISTANCE);
      }

      return options;
    }, []);

    useEffect(() => {
      if (!isSubmitting && submitCount <= 0 && !defaultState) {
        setValue('location', FILTER_KEYS.USER_LOCATION, {
          shouldValidate: true,
          shouldTouch: true,
        });
        // @ts-ignore
        handleSubmit(onSubmit)();
      }
    }, [isSubmitting, submitCount, defaultState]);

    useEffect(() => {
      const subscription = watch((_: SearchFormFields, { name }) => {
        switch (name) {
          case 'search':
            // @ts-ignore
            return debounce(handleSubmit(onSubmit)(), 1000);
          case 'location':
            // @ts-ignore
            return handleSubmit(onSubmit)();
          default:
            return;
        }
      });

      return () => subscription.unsubscribe();
    }, [handleSubmit, onSubmit, watch]);

    return (
      <>
        <Wrap
          {...{
            className:
              'animate-fade-in sm:grid-cols-2 lg:grid-cols-3 !gap-4 md:!gap-6 py-6 lg:pt-0',
            size: 'large',
            spacer: false,
          }}
        >
          <div className="grid grid-cols-[21px_auto] gap-x-2 items-center">
            <Image src="/search.svg" width={21} height={21} alt="" />
            <TextField
              {...{
                className: 'border-0 p-0',
                label: translations['Search'],
                labelVisible: false,
                name: 'search',
                placeholder: translations['Search by name or location'],
              }}
            />
          </div>
          <Select
            {...{
              className:
                'justify-self-start self-center border-0 py-0 pl-8 sm:pl-6 -ml-4 mr-12 sm:ml-0 md:mr-0 partner-state-select',
              icon: '/pin-outline.svg',
              label: translations['State'],
              labelVisible: false,
              name: 'location',
              placeholder: translations['Select State'],
              options: [
                {
                  label: translations['Current Location'],
                  value: FILTER_KEYS.USER_LOCATION,
                },
                ...statesList,
              ],
            }}
          />
        </Wrap>
        <Wrap
          {...{
            className:
              'absolute top-full justify-items-start z-10 mt-6 !gap-y-6 pointer-events-none max-w-none',
            spacer: false,
          }}
        >
          <Button
            {...{
              'aria-controls': `#${filtersId}`,
              className: 'pointer-events-auto shadow-lg',
              label: (
                <>
                  <Image
                    className="align-middle inline-block mr-2"
                    src="/filter.svg"
                    alt={translations['Close Filters']}
                    width={16}
                    height={14}
                  />
                  {translations['Filter']}
                </>
              ),
              onClick: () => toggleFilters(!filtersVisible),
              rounded: true,
              size: 'small',
              type: 'button',
              variant: 'light',
            }}
          />
          <Card
            {...{
              'aria-hidden': !filtersVisible,
              className: [
                'shadow-lg transition-all relative pointer-events-auto',
                filtersVisible ? '' : 'invisible opacity-0',
              ].join(' '),
              filled: true,
              id: filtersId,
              rounded: true,
              tagName: 'section',
              variant: 'white',
            }}
          >
            <span
              {...{
                className:
                  'absolute bottom-full left-7 w-0 h-0 border-transparent border-b-white border-[1rem]',
              }}
            ></span>
            <div
              {...{
                className: 'flex items-center justify-between',
              }}
            >
              <HeadingBody
                {...{
                  title: translations['Filter results'],
                  level: '4',
                  tagName: 'header',
                }}
              />
              <Link
                {...{
                  'aria-controls': `#${filtersId}`,
                  className: 'w-8 h-8 flex items-center justify-end',
                  onClick: () => toggleFilters(!filtersVisible),
                  size: 'small',
                }}
              >
                <Image
                  src="/close.svg"
                  className="w-4"
                  alt={translations['Close Filters']}
                  width={15}
                  height={15}
                />
              </Link>
            </div>
            <Switch
              {...{
                classNameWrap: 'grid-flow-col justify-between',
                label: translations['Open now'],
                highlight: 'secondary',
                name: 'open',
              }}
            />
            <Toggle
              {...{
                classNameWrap: 'grid-cols-2 gap-x-6',
                label: translations['Sort by'],
                highlight: 'secondary',
                name: 'sort',
                options: sortOptions,
                type: 'radio',
                validations: ['required'],
              }}
            />
            <div {...{ className: 'grid gap-y-3' }}>
              <Button
                {...{
                  label: translations['Filter results'],
                  rounded: true,
                  type: 'submit',
                  variant: 'tertiary',
                }}
              />
              <Link
                {...{
                  className: 'justify-self-center',
                  onClick: () => {
                    onReset();
                    reset();
                  },
                  variant: 'underline',
                }}
              >
                {translations['Clear filters']}
              </Link>
            </div>
          </Card>
        </Wrap>
      </>
    );
  },
);

const LocationsList: React.FC<
  Pick<LocationsSettings, 'activeId'> & {
    locations?: LocationItems;
    onChange: (id: LocationItem['id']) => void;
  }
> = ({ activeId, locations = [], onChange }) => {
  const { translations } = useWordPressContext();

  const getActiveIndexyById = useCallback(
    (id: LocationItem['id']) =>
      locations.findIndex(location => location.id === id),
    [locations],
  );

  const getActiveIdByIndex = useCallback(
    (index: string) => locations?.[index]?.id,
    [locations],
  );

  const activeSlide = useMemo(
    () => getActiveIndexyById(activeId),
    [activeId, getActiveIndexyById],
  );

  return (
    <Carousel
      className="w-full lg:absolute lg:left-0 lg:bottom-0 lg:flex lg:items-end md:my-10 lg:mt-0 lg:mb-6 md:px-10 locations-carousel lg:h-88 lg:pt-12"
      slideClassName="mr-4"
      loop={false}
      showPagination={false}
      activeSlide={activeSlide}
      onSlideChange={(index: number) => {
        onChange(getActiveIdByIndex(index.toString()));
      }}
    >
      {locations?.length > 0 ? (
        locations.map(
          (
            {
              category,
              features,
              formattedAddress,
              id,
              name,
              openingHours,
              rating,
              state,
              uri,
            },
            index,
          ) => {
            const isActive = activeSlide === index;
            return (
              <LocationCard
                key={index}
                {...{
                  category,
                  features,
                  formattedAddress,
                  id,
                  name,
                  openingHours,
                  rating,
                  state,
                  uri,
                  isActive,
                  onChange,
                }}
              />
            );
          },
        )
      ) : (
        <Card
          {...{
            'aria-live': 'polite',
            className: 'w-80 md:w-136 mb-10 h-fit place-self-end',
            filled: true,
            rounded: true,
            variant: 'white',
          }}
        >
          {
            translations[
              'We’re sorry, but we couldn’t find results, try doing a different search.'
            ]
          }
        </Card>
      )}
    </Carousel>
  );
};

const LocationsSearchContent: React.FC<
  Pick<LocationsSettings, 'activeId' | 'activeLocations' | 'center'> &
    Pick<LocationsSearchProps, 'variant'> & {
      updateActiveId: (id: LocationItem['id']) => void;
    }
> = memo(({ activeId, activeLocations, center, updateActiveId, variant }) => {
  return (
    <>
      <LocationsMap
        {...{
          center,
          className:
            'animate-fade-in h-1/2-screen-height lg:h-full grow bg-gray-200',
          locations: activeLocations,
          onSelect: updateActiveId,
          variant,
          activeId,
          zoom: 10,
        }}
      />
      <LocationsList
        {...{
          activeId,
          onChange: updateActiveId,
          locations: activeLocations,
        }}
      />
    </>
  );
});

const LocationsSearch: React.FC<LocationsSearchProps> = ({
  locations,
  variant,
}) => {
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const defaultState = useCurrentState();
  const { translations } = useWordPressContext();
  const { addToast } = useToast();
  const { isRetrieving: userLocationLoading, ...userLocation } =
    useUserLocation();

  const initialData = useMemo<LocationsSettingsInit>(() => {
    const hasLocation = defaultState;

    return {
      activeLocations: locations,
      filters: {
        location: hasLocation ? defaultState || '' : FILTER_KEYS.USER_LOCATION,
        open: false,
        search: '',
        sort: SORT_KEYS.NAME,
      },
      ...(hasLocation
        ? {}
        : { center: { lat: userLocation.lat, lng: userLocation.lng } }),
    };
  }, [defaultState, locations, userLocation]);

  const [{ activeId, activeLocations, center, loading }, updateSettings] =
    useReducer(locationSettingsReducer, initialData, initReducer);

  const onError = useCallback(
    (message?: string) =>
      addToast({
        content:
          translations?.[message] ||
          translations['An error occurred, please try again.'],
        key: 'user-review-error',
        type: 'negative',
        shouldRemove: true,
      }),
    [addToast, translations],
  );

  const onReset = useCallback(
    () =>
      updateSettings({
        type: 'reset',
        value: initialData,
      }),
    [initialData, updateSettings],
  );

  const onSearchSubmit = useCallback(
    async (values: SearchFormFields) => {
      try {
        const isCurrLocation = values.location === FILTER_KEYS.USER_LOCATION;

        if (isCurrLocation && !userLocation?.lat) {
          throw {
            message:
              'You need to allow access to your location in your browser settings.',
          };
        }

        updateSettings({
          type: 'updateSearch',
          value: {
            center: isCurrLocation
              ? (userLocation as LocationCoordinates)
              : null,
            filters: values,
          },
        });

        const variables = getQueryVariables(values);

        if (Object.keys(variables).length) {
          const { data, error } = await queryResearch<
            PartnersLocationsSearchQuery,
            PartnersLocationsSearchQueryVariables
          >(client, refetchPartnersLocationsSearchQuery(variables));

          if (error) {
            throw error;
          }

          let newLocations = [];

          if (data?.nodes?.length) {
            newLocations = data?.nodes.map(({ id }) =>
              locations.find(item => item.id === id),
            );
          }

          locations = newLocations;
        }

        updateSettings({
          type: 'updateLocations',
          value: locations,
        });
      } catch (error) {
        onError(error.message);
      }
    },
    [locations, onError, updateSettings, userLocation],
  );

  const updateActiveId = useCallback(
    (id: LocationItem['id']) =>
      updateSettings({ type: 'updateActiveId', value: id }),
    [updateSettings],
  );

  return (
    <section className="min-h-screen-height-header-full relative flex flex-col">
      <Form
        {...{
          className: 'relative',
          defaultValues: initialData.filters,
          onSubmit: onSearchSubmit,
        }}
      >
        <SearchFormContent
          {...{
            onReset,
            onSubmit: onSearchSubmit,
            translations,
            defaultState,
            userLocation: userLocation as LocationCoordinates,
          }}
        />
      </Form>
      {loading || userLocationLoading ? (
        <Loader {...{ className: 'grow', fullHeight: false }} />
      ) : (
        <LocationsSearchContent
          {...{
            activeId,
            activeLocations,
            center,
            updateActiveId,
            variant,
          }}
        />
      )}
    </section>
  );
};

export default function (props) {
  const apolloClient = initializeWpApollo();

  return (
    <ApolloProvider client={apolloClient}>
      <LocationsSearch {...props} />;
    </ApolloProvider>
  );
}
